/**
 * Phase 41 (Plan 41-03 Task 1) — BellDropdown primitive for the v7 TopBar.
 *
 * Composes:
 *   - useNotifications({ limit: 20 })  → polled list + unreadCount badge
 *   - useMarkNotificationRead          → flip a single row
 *   - useMarkAllNotificationsRead      → bulk flip
 *   - useClickOutside (Plan 41-02)     → close on outside click
 *
 * Renders user-supplied `payload.message` strings as text nodes only (React
 * default escaping; no `dangerouslySetInnerHTML` — T-41-08 mitigation). The
 * "View all →" link is hard-coded to `/notifications` (T-41-09 mitigation;
 * never extracts a URL from payload).
 *
 * Visual port from .planning/v7-design-handoff/v7-sla/shell.jsx lines 117-154.
 */
import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { BellIcon } from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
} from '../../hooks/useNotifications';
import { useClickOutside } from '../../hooks/useClickOutside';

const KIND_LABEL: Record<string, string> = {
  'sla.warning': 'SLA warning',
  'sla.breach': 'SLA breach',
  'package.threshold_crossed': 'Package alert',
  'package.cap_blocked': 'Package cap reached',
};

export function BellDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { data } = useNotifications({ limit: 20 });
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  useClickOutside(ref, () => setOpen(false));

  const unread = data?.unreadCount ?? 0;
  const items = (data?.data ?? []).slice(0, 20);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Notifications"
        className="relative p-2 rounded-md hover:bg-ms-surface-2 text-ms-text-secondary"
      >
        <BellIcon className="h-5 w-5" />
        {unread > 0 && (
          <span
            data-testid="bell-unread-badge"
            className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[9.5px] font-bold flex items-center justify-center"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-[380px] bg-white rounded-lg border border-ms-border shadow-xl z-40 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-ms-border">
            <span className="text-[12.5px] font-semibold text-ms-text-strong">Notifications</span>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => markAll.mutate()}
                className="text-[11px] font-medium text-ms-orange hover:opacity-80"
              >
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[320px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-6 text-center text-[12px] text-ms-text-muted">
                No notifications
              </div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => {
                    if (!n.readAt) markRead.mutate(n.id);
                  }}
                  className={`w-full text-left px-4 py-3 border-b border-ms-border-subtle hover:bg-ms-surface-2 ${
                    n.readAt ? '' : 'bg-ms-orange/5'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11.5px] font-semibold text-ms-text-strong">
                      {KIND_LABEL[n.kind] ?? n.kind}
                    </span>
                    <span className="text-[10.5px] text-ms-text-muted">
                      {formatDistanceToNow(new Date(n.createdAt), { addSuffix: true })}
                    </span>
                  </div>
                  {typeof n.payload?.message === 'string' && (
                    <div className="text-[11.5px] text-ms-text-secondary mt-0.5 line-clamp-2">
                      {n.payload.message as string}
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
          <div className="px-4 py-3 border-t border-ms-border flex justify-between items-center">
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              className="text-[11.5px] font-semibold text-ms-orange hover:opacity-80"
            >
              View all →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
